import Big, {BigSource} from 'big.js';
import {combineLatest} from 'rxjs';
import {map, throttleTime, switchMap} from 'rxjs/operators';
import {RiskMode} from '@/enum';
import {eq, gt, max, min, minus, times, toString} from '@/helper/num';
import {contractUtil} from '@/helper/contract';
import {calcMaintenanceMargin} from '@/helper/leverage';
import {contractDefaultTakerFeeRateEr$, contractsCache$, leverageMarginsCache$} from '@/config';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {InnerContractAccount, innerContractAccounts$} from '@/contract-accounts/inner-contract-accounts';
import {usdtRiskModeRas$} from '@/ras/ras';
import {calcUnrealisedPnlEv, calcValueRv} from '@/calc';
import {calcAccountCrossPosCost, calcCrossPosCost, calcFixedUsedBalance} from './calc';
import {calcCrossPosMmCostV2, calcHedgedPosMM, calcNormalPosMMCost} from './margin-ratio';
import {positionsCache$ as positionsCache0$} from './positions-cache';
import type {ws, ContractPair, LeverageMargin, ContractMarket, PosSide, Side} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;

const positionsCache2$ = combineLatest([
  positionsCache0$,
  innerContractAccounts$,
  leverageMarginsCache$,
  contractMarketRealtimeCache$,
  contractsCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(
  throttleTime(150),
  map(([positionsCache, innerContractAccounts, marginCache, marketsCache, contractsCache, defaultTakerFeeEr]) => {
    const positions = positionsCache.toArray<ContractPositionRaw>();
    const accountCurrenciesV2 = contractsCache
      .toArray<ContractPair>()
      .filter((o) => contractUtil.isPerpetualV2Contract(o.type))
      .reduce((p, c) => (p.includes(c.settleCurrency) ? p : [...p, c.settleCurrency]), []);
    const crossPosMmCostV2Cache = accountCurrenciesV2.reduce((p, c) => {
      return p.set(c, calcCrossPosMmCostV2(c, positions, marginCache, contractsCache, marketsCache, defaultTakerFeeEr));
    }, new Map());
    const totalCrossPosCostV2Cache = accountCurrenciesV2.reduce((p, c) => {
      return p.set(c, calcTotalCrossPosImCost(c, positions, marketsCache, contractsCache, defaultTakerFeeEr));
    }, new Map());
    const accountsCache = innerContractAccounts.data;
    const totalCollateralAvaiValueRv = innerContractAccounts.totalCollateralAvaiValueRv0;
    return positions
      .map((position) => {
        const contract = contractsCache.get(position.symbol);
        if (position.isCross && position.size > 0) {
          // 联合保证金模式，全仓仓位，前端计算margin、liq price: https://cmex-pro.jp.larksuite.com/wiki/G6tJwCJSbi3EOsk59X6jUJ8QpBc
          let positionMarginEv = position.positionMarginEv;
          let liquidationPriceEp: number | string = position.liquidationPriceEp;

          const account = accountsCache.get(contract.settleCurrency);
          const market = marketsCache.get(position.symbol);
          const curPositions = positions.filter((item) => item.symbol === position.symbol);
          if (position.isCross && position.size > 0) {
            liquidationPriceEp = calcLiqPriceEp(
              position,
              curPositions,
              account,
              totalCollateralAvaiValueRv,
              contract,
              market,
              marginCache,
              contractsCache,
              marginCache,
              defaultTakerFeeEr
            );
            if (position.type === 'PerpetualV2') {
              const totalCrossMmCost = crossPosMmCostV2Cache.get(contract.settleCurrency);
              const totalCrossPosImCost = totalCrossPosCostV2Cache.get(contract.settleCurrency);
              positionMarginEv = calcCrossPosMarginV2(
                account,
                position,
                curPositions,
                totalCrossMmCost,
                totalCrossPosImCost,
                totalCollateralAvaiValueRv,
                marginCache,
                contractsCache,
                marketsCache,
                defaultTakerFeeEr
              ).toNumber();
            } else {
              positionMarginEv = calcCrossPosMarginV1(
                position,
                account,
                market,
                contract,
                defaultTakerFeeEr
              ).toNumber();
            }
          }
          return {...position, liquidationPriceEp, positionMarginEv};
        }
        return position;
      })
      .reduce((p, c) => {
        const {type, symbol, posSide} = c;
        const key = type === 'Perpetual' ? symbol : symbol + posSide;
        p.set(key, c);
        return p;
      }, new Map());
  })
);

function calcTotalCrossPosImCost(currency0, positions, marketsCache, contractsCache, defaultTakerFeeEr) {
  const positions2 = positions.filter(({currency, size, isCross}) => currency === currency0 && isCross && size > 0);
  return positions2.length > 0
    ? calcAccountCrossPosCost(positions2, marketsCache, contractsCache, defaultTakerFeeEr)
    : Big(0);
}

export const positionsCache$ = usdtRiskModeRas$.pipe(
  switchMap((riskMode) => (riskMode === RiskMode.Classic ? positionsCache0$ : positionsCache2$))
);

// 计算反向合约全仓margin，逐仓不需要前端计算
function calcCrossPosMarginV1(
  position: ContractPositionRaw,
  account: InnerContractAccount,
  market: ContractMarket,
  contract: ContractPair,
  defaultTakerFeeEr: number
) {
  if (!position || position.size <= 0 || !position.isCross) {
    return Big(0);
  }
  const markPriceEp = market ? market.markPriceEp : 0;
  const pnl = appendUnrealisedPnl(position, markPriceEp, contract);
  const crossMarginRv = Big(account.accountBalanceEv)
    .minus(account.totalDebtEv)
    .minus(account.totalInterestEv)
    .plus(pnl)
    .minus(account.frozenAsCollateralEv || 0)
    .minus(account.totalOrdUsedBalanceEv);

  const crossPosCostRv = calcCrossPosCost([position], market, contract, defaultTakerFeeEr);
  const bankruptCommEv = calcBankruptCommEv(contract, position, market, defaultTakerFeeEr);
  return Big(min(crossMarginRv, crossPosCostRv)).minus(bankruptCommEv);
}

function calcCrossPosMarginV2(
  account: InnerContractAccount,
  position: ContractPositionRaw,
  positions0: ContractPositionRaw[],
  totalCrossPosMmCost,
  totalCrossPosImCost,
  totalCollateralAvaiValueRv,
  marginCache: Map<string, LeverageMargin>,
  contractsCache: Map<string, ContractPair>,
  marketsCache: Map<string, ContractMarket>,
  defaultTakerFeeEr
) {
  // PosCrossMmCost: 当前仓位 net pos mm
  // CcyCrossSharedEquity: 全仓共享保证金
  // PosImCost:  当前仓位 net pos mi
  // PosMmCost:  当前仓位 net pos mm
  // PosBankruptComm:  当前仓位 net pos mm
  // PosHedgedMm:  当前仓位 hedge pos mm
  // CcyTotalPosCostBalance: 所有 net pos mi
  // CcyTotalPosNetMmCost: 所有 net pos mm

  const positions = positions0.filter((o) => o.isCross && o.size > 0);
  if (positions.length <= 0 || position.size <= 0) {
    return Big(0);
  }
  let posMmCost: BigSource = 0;
  let posHedgedMm: BigSource = 0;

  const isOneWay = positions.every(({posMode}) => posMode === 'OneWay');
  const contract = contractsCache.get(position.symbol);
  const market = marketsCache.get(position.symbol);
  const posImCost = calcCrossPosCost(positions, market, contract, defaultTakerFeeEr, position.posSide);

  const totalMarginDeltaEv = minus(totalCrossPosImCost, totalCrossPosMmCost);
  if (isOneWay) {
    const {mmCost} = calcNormalPosMMCost(positions[0], marginCache, contractsCache, marketsCache, defaultTakerFeeEr);
    posMmCost = mmCost;
  } else {
    const {lessPos} = getMoreLessPos(positions[0], positions[1]);
    if (!lessPos || lessPos.posSide !== position.posSide) {
      const posLong = positions.find(({posSide}) => posSide === 'Long');
      const posShort = positions.find(({posSide}) => posSide === 'Short');
      const {mmCost} = calcHedgedPosMM(posLong, posShort, marginCache, contractsCache, marketsCache, defaultTakerFeeEr);
      posMmCost = mmCost;
    }
    if (lessPos) {
      posHedgedMm = calcFixedUsedBalance(position.symbol, lessPos, contractsCache, marginCache, defaultTakerFeeEr);
    }
  }
  const crossSharedMarginEv = calcCrossShareMargin(account, totalCollateralAvaiValueRv, totalCrossPosMmCost);

  const posMargin0 = eq(totalMarginDeltaEv, 0)
    ? Big(0)
    : Big(crossSharedMarginEv)
        .times(gt(crossSharedMarginEv, 0) ? Big(posImCost).minus(posMmCost) : posMmCost)
        .div(totalMarginDeltaEv);
  const posMargin1 = Big(posMmCost).plus(posMargin0);
  const bankruptCommEv = calcBankruptCommEv(contract, position, market, defaultTakerFeeEr);
  return Big(min(posMargin1, posImCost)).minus(bankruptCommEv).plus(posHedgedMm);
}

function getMoreLessPos(pos1, pos2): {lessPos: ContractPositionRaw; morePos: ContractPositionRaw} {
  const longSize = pos1?.size || 0;
  const shortSize = pos2?.size || 0;

  return {
    morePos: longSize > shortSize ? pos1 : pos2,
    lessPos: longSize > shortSize ? pos2 : pos1,
  };
}

function appendUnrealisedPnl(position: ContractPositionRaw, exitPriceEp, contract) {
  if (!contract) {
    return 0;
  }
  const {contractSide, contractSize, valueFactor, priceFactor} = contract;
  return calcUnrealisedPnlEv({
    side: position.side,
    contractSide,
    contractSize,
    qty: position.size,
    entryPriceEp: position.avgEntryPriceEp,
    exitPriceEp,
    factor: valueFactor,
    priceFactor,
  });
}

function calcCrossShareMargin(
  account: InnerContractAccount,
  totalCollateralAvaiValueRv: BigSource,
  crossNetPosMMCostEv: BigSource
) {
  const totalCollateralAvaiEv = account.canBorrow
    ? Big(totalCollateralAvaiValueRv)
        .div(account.indexPriceRp)
        .times(account.valueFactor)
        .plus(account.occupationBalanceEv)
    : '0';
  return Big(account.crossEquityEv || 0)
    .plus(totalCollateralAvaiEv)
    .minus(account.frozenAsCollateralEv || 0)
    .minus(crossNetPosMMCostEv)
    .minus(account.totalOrdUsedBalanceEv || 0)
    .minus(account.crossFixedUsedBalanceEv || 0);
}

function calcBankruptCommEv(
  contract,
  position: ContractPositionRaw,
  market: ContractMarket,
  defaultTakerFeeRr: number | string
) {
  let size = position.size;
  const posVal = calcValueRv(
    contract.contractSide,
    contract.contractSize,
    contract.priceFactor,
    size,
    market ? market.markPriceEp : 0
  );
  const isBuy = position.side === 'Buy';
  const mmReqRr = Big(position.maintMarginReqEr).div(contract.ratioFactor);
  const rate = Big(1)
    .plus(isBuy ? mmReqRr : 0)
    .minus(isBuy ? 0 : mmReqRr)
    .times(defaultTakerFeeRr);

  return toString(Big(posVal).times(rate).times(contract.valueFactor));
}

function calcLiqPriceEp(
  position: ContractPositionRaw,
  curPositions: ContractPositionRaw[],
  account: InnerContractAccount,
  totalCollateralAvaiValueRv: BigSource,
  contract: ContractPair,
  market: ContractMarket,
  marginsCache: Map<string, LeverageMargin>,
  contractsCache,
  marginCache,
  defaultTakerFeeRr
) {
  const positions = curPositions.filter(({size}) => size > 0);
  let newSize: BigSource = position.size;
  let fixedUsedBalanceRv: BigSource = 0;
  const positions2 = positions.filter((o) => ['Long', 'Short'].includes(o.posSide) && o.posMode === 'Hedged');
  if (positions2.length > 1) {
    const {lessPos} = getMoreLessPos(positions2[0], positions2[1]);
    if (position.posSide === lessPos.posSide) {
      return contract.tickSizeEp;
    }
    fixedUsedBalanceRv = calcFixedUsedBalance(position.symbol, lessPos, contractsCache, marginCache, defaultTakerFeeRr);
    newSize = minus(position.size, lessPos.size);
  }
  const markPriceEp = market ? market.markPriceEp : 0;
  const posValueRv = calcValueRv(
    contract.contractSide,
    contract.contractSize,
    contract.priceFactor,
    newSize,
    markPriceEp
  );
  const {maintenanceMarginRateRr, maintenanceAmountRv} = calcMaintenanceMargin(contract, position, marginsCache);

  const totalCollateralAvaiEv = account.canBorrow
    ? Big(totalCollateralAvaiValueRv)
        .div(account.indexPriceRp)
        .times(account.valueFactor)
        .plus(account.occupationBalanceEv)
    : '0';
  const rate =
    contract.contractSide > 0 && gt(account.totalPosMarginEv, 0)
      ? Big(position.crossMarginWeightEv || 0).div(account.totalPosMarginEv)
      : 1;

  const maxPosMarginRv = Big(account.crossEquityEv || 0)
    .minus(account.frozenAsCollateralEv || 0)
    .plus(totalCollateralAvaiEv)
    .minus(account.totalOrdUsedBalanceEv || 0)
    .div(contract.valueFactor)
    .times(rate);
  const liqPriceRp = calcLiqPriceRp(
    contract,
    position.side,
    newSize,
    posValueRv,
    fixedUsedBalanceRv,
    maxPosMarginRv,
    maintenanceMarginRateRr,
    maintenanceAmountRv,
    defaultTakerFeeRr
  );
  return max(times(liqPriceRp, contract.priceFactor), contract.tickSizeEp);
}

function calcLiqPriceRp(
  contract: ContractPair,
  posSide: Side,
  newSize,
  posValueRv,
  fixedUsedBalanceRv,
  maxPosMarginRv,
  maintenanceMarginRateRr,
  maintenanceAmountRv,
  defaultTakerFeeRr
) {
  if (contract.contractSide > 0) {
    // maxPosMargin: 逐仓：Pos::assignedPosBalance + unpnlPosFixedUsedBalance: fully hedged 部分的仓位占用
    // 对于FullyHedged 仓位，liqPrice可以设置为“-”，即不爆仓
    // 正向合约
    if (posSide === 'Buy') {
      // Buy: (netMarkValue + PosFixedUsedBalance - maxPosMargin -mma) / (1 - defaultTakerFeeRate - mmr) / PosSize
      return Big(posValueRv)
        .plus(fixedUsedBalanceRv)
        .minus(maxPosMarginRv)
        .minus(maintenanceAmountRv)
        .div(Big(1).minus(defaultTakerFeeRr).minus(maintenanceMarginRateRr))
        .div(newSize);
    }
    if (posSide === 'Sell') {
      // Sell: (netMarkValue - PosFixedUsedBalance + maxPosMargin + mma) / (1 + defaultTakerFeeRate + mmr) / PosSize
      return Big(posValueRv)
        .minus(fixedUsedBalanceRv)
        .plus(maxPosMarginRv)
        .plus(maintenanceAmountRv)
        .div(Big(1).plus(defaultTakerFeeRr).plus(maintenanceMarginRateRr))
        .div(newSize);
    }
  }
  // 反向合约
  if (posSide === 'Buy') {
    // Buy: PosSize * (1 + defaultTakerFeeRate + mmr) / (markValue + maxPosMargin + mma)
    return Big(newSize)
      .times(Big(1).plus(defaultTakerFeeRr).plus(maintenanceMarginRateRr))
      .div(Big(posValueRv).plus(maxPosMarginRv).plus(maintenanceAmountRv));
  }
  if (posSide === 'Sell') {
    // Sell: PosSize * (1 - defaultTakerFeeRate - mmr) / (markValue - maxPosMargin - mma)
    return Big(newSize)
      .times(Big(1).minus(defaultTakerFeeRr).minus(maintenanceMarginRateRr))
      .div(Big(posValueRv).minus(maxPosMarginRv).minus(maintenanceAmountRv));
  }
  return Big(0);
}
